import i18n from 'app/i18n';
import { DIALOGS } from './chat-screen-state';

export function moderationActions() {
  return {
    // ─────────────────────────────────────────
    // Reporting
    // ─────────────────────────────────────────
    resetReportDialog() {
      this.reportingMessage = null;
      this.reportReasons = [];
      this.reportDescription = '';
      this.reportFollowStatus = false;
      this.reportBlock = false;
      this.showBlockConfirm = false;
      this.reportError = null;
      this.isSubmittingBlock = false;
    },

    openReportDialog(message) {
      this.resetReportDialog();
      this.reportingMessage = message;
      this.dialog = DIALOGS.REPORT;
    },

    showReportToast(blocked) {
      this.reportSuccess = true;
      this.reportBlocked = blocked;
      clearTimeout(this._reportToastTimer);
      this._reportToastTimer = setTimeout(() => { this.reportSuccess = false; }, 10000);
    },

    closeReportDialog() {
      if (this.showBlockConfirm && !this.reportSuccess && this.reportingMessage) {
        this.showReportToast(false);
      }
      this.resetReportDialog();
      this.dialog = null;
      this.blockingConversationId = null;
    },

    async confirmBlock() {
      const fromReport = !!this.reportingMessage;
      const conv = this.getConversation(this.blockingConversationId);

      // In a group, block the reported message's author; in a DM, block the other participant
      const targetUser = conv?.other_participant ?? (fromReport ? this.reportingMessage?.user : null);
      if (!targetUser) return;

      this.isSubmittingBlock = true;
      try {
        await this.$api({
          url: '/chat/blocks',
          method: 'POST',
          data: { blocked_id: targetUser.id }
        });
        this.trackChat('moderation', 'block');
        this.rememberBlockedUserId(targetUser.id);

        // Only mark the conversation itself as blocked for DMs — groups stay accessible
        if (conv?.other_participant) {
          this.moveConversationToBlocked(conv);
        }

        if (fromReport) {
          this.showReportToast(true);
          this.closeReportDialog();
        } else {
          const name = targetUser.name;
          this.blockSuccessMessage = i18n.t('chat.report.toast_blocked', { name });
          this.blockSuccess = true;
          clearTimeout(this._blockToastTimer);
          this._blockToastTimer = setTimeout(() => { this.blockSuccess = false; }, 10000);
          this.closeBlockDialog();
        }
      } catch (err) {
        console.error('Failed to block user:', err);
      } finally {
        this.isSubmittingBlock = false;
      }
    },

    async submitReport() {
      if (this.reportReasons.length === 0) {
        this.reportError = i18n.t('chat.report.error_select_reason');
        return;
      }

      if (this.reportReasons.includes('other') && !this.reportDescription.trim()) {
        this.reportError = i18n.t('chat.report.error_provide_description');
        return;
      }

      this.isSubmittingReport = true;
      this.reportError = null;

      try {
        await this.$api({
          url: `/chat/conversations/${this.activeConversationId}/messages/${this.reportingMessage.id}/reports`,
          method: 'POST',
          data: {
            reasons: this.reportReasons,
            description: this.reportDescription.trim() || null,
            follow_status: this.reportFollowStatus
          }
        });

        this.trackChat('moderation', 'report');
        this.reportingMessage.reported_by_me = true;

        if (this.reportBlock) {
          this.blockingConversationId = this.activeConversationId;
          this.showBlockConfirm = true;
        } else {
          this.showReportToast(false);
          this.closeReportDialog();
        }
      } catch (err) {
        console.error('Failed to submit report:', err);
        if (err instanceof Response) {
          try {
            const data = await err.json();
            this.reportError = data.errors?.[0] || i18n.t('chat.report.error_submit_failed');
          } catch {
            this.reportError = i18n.t('chat.report.error_submit_failed');
          }
        } else {
          this.reportError = i18n.t('chat.report.error_submit_failed');
        }
      } finally {
        this.isSubmittingReport = false;
      }
    },

    // ─────────────────────────────────────────
    // Blocking
    // ─────────────────────────────────────────
    async loadBlockedUsers() {
      try {
        const response = await this.$api({
          url: '/chat/blocks',
          method: 'GET'
        });
        this.blockedUserIds = response.data.blocks.map((b) => b.blocked_id);
      } catch (err) {
        console.error('Failed to load blocked users:', err);
      }
    },

    isUserBlocked() {
      const conv = this.getConversation(this.activeConversationId);
      if (!conv || conv.conversation_type === 'group') return false;
      if (conv.blocked_by_me) return true;
      return conv.other_participant && this.blockedUserIds.includes(conv.other_participant.id);
    },

    // They blocked us — we can still see the composer, but sends stay local.
    isBlockedByOther() {
      const conv = this.getConversation(this.activeConversationId);
      if (!conv || conv.conversation_type === 'group') return false;
      return !!conv.is_blocked && !this.isUserBlocked();
    },

    areMessageActionsHidden() {
      return this.isUserBlocked() || this.isBlockedByOther();
    },

    // Hide the composer when we blocked them (or the group is unusable).
    // If they blocked us, the composer stays so we can show the undeliverable notice.
    isConversationBlocked() {
      return this.isUserBlocked() || this.isRemovedFromGroup() || this.isGroupDeactivated();
    },

    openBlockDialog(convId = null) {
      const conversationId = convId ?? this.activeConversationId;
      const conv = this.getConversation(conversationId);

      if (convId) {
        this.closeListMenu();
      }

      if (!conv || !conv.other_participant) {
        this.blockError = 'Unable to open block dialog for this conversation';
        this.error = this.blockError;
        return;
      }

      this.blockingConversationId = conversationId;
      this.blockError = null;
      this.error = null;
      this.showBlockConfirm = true;
    },

    closeBlockDialog() {
      this.showBlockConfirm = false;
      this.blockingConversationId = null;
      this.blockError = null;
    },

    getBlockingUserName() {
      if (!this.blockingConversationId) return '';
      const conv = this.getConversation(this.blockingConversationId);
      return conv?.other_participant?.name ?? this.reportingMessage?.user?.name ?? 'this user';
    },

    openUnblockDialog() {
      const conv = this.getConversation(this.activeConversationId);

      if (!conv || !conv.other_participant) return;

      this.blockingConversationId = this.activeConversationId;
      this.blockError = null;
      this.showUnblockConfirm = true;
    },

    closeUnblockDialog() {
      this.showUnblockConfirm = false;
      this.blockingConversationId = null;
      this.blockError = null;
    },

    async submitUnblock() {
      const conv = this.getConversation(this.blockingConversationId);
      if (!conv?.other_participant) return;

      this.isSubmittingBlock = true;
      this.blockError = null;

      try {
        // Find the block ID
        const blocksResponse = await this.$api({
          url: '/chat/blocks',
          method: 'GET'
        });
        const block = blocksResponse.data.blocks.find((b) => b.blocked_id === conv.other_participant.id);

        if (block) {
          await this.$api({
            url: `/chat/blocks/${block.id}`,
            method: 'DELETE'
          });
        }

        this.blockedUserIds = this.blockedUserIds.filter((id) => id !== conv.other_participant.id);
        this.moveConversationFromBlocked(conv);
        this.closeUnblockDialog();
        await this.reloadConversation(conv.id);
      } catch (err) {
        console.error('Failed to unblock user:', err);
        this.blockError = 'Failed to unblock user';
      } finally {
        this.isSubmittingBlock = false;
      }
    }
  };
}